import { useGetListings } from 'hooks/useGetListings';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { gqlUpdateOrder } from './graphql/mutations';
import { getOrderConfiguration, GetOrderConfigurationsType } from './graphql/queries';
import { DatesAndConfigurationForm, EstateAddress, ParsedAmountEntry } from './services';
import { zodResolver } from '@hookform/resolvers/zod';
import { datesAndConfigValidationSchema } from './services/schemas';
import { useAccountingUpdateSocketEvent } from "hooks/useAccountingUpdateFromSocket";
import { useInfoUpdateEvent } from 'hooks/useInfoUpdateEvent';
import useAlert from 'utils/alert';

const AUTOSAVE_DELAY_MS = 500;
const AUTOSAVE_FIELDS: ReadonlySet<keyof DatesAndConfigurationForm> = new Set([
  'acceptance_date',
  'closing_date',
  'closing_place',
  'contract_date',
  'disbursement_date',
  'funding_date',
  'status',
  'additional_fees_letter',
  'transaction_type',
  'type',
  'workflow',
]);
const AMOUNT_CODE_REGEX = /^(loan_amount|purchase_price)_/;

const useDatesAndConfig = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const alert = useAlert();

  const queryCache = useQueryClient();
  useAccountingUpdateSocketEvent({
    callback: () => queryCache.invalidateQueries(['order-amounts-info', orderId])
  });

  const methods = useForm<DatesAndConfigurationForm>({
    defaultValues: {
      order_id: '',
      acceptance_date: '',
      closing_date: '',
      contract_date: '',
      disbursement_date: '',
      funding_date: '',
      transaction_type: '',
      type: '',
      workflow: '',
      heloc: false,
      construction_loan: false,
      cash_only: false,
      closing_place: '',
      status: '',
      additional_fees_letter: '',
    },
    resolver: zodResolver(datesAndConfigValidationSchema),
  });
  const { getValues, reset, trigger, watch } = methods;
  const autosaveTimerRef = useRef<number | null>(null);
  const autosaveVersionRef = useRef(0);
  const hasHydratedRef = useRef(false);
  const isHydratingRef = useRef(false);

  const { data: configurationOrder } = useQuery<GetOrderConfigurationsType>(
    ['order-configuration', orderId],
    () => getOrderConfiguration({ id: orderId, filters: { accounting: '/^(loan_amount|purchase_price)_/' } }),
    {
      refetchOnWindowFocus: false,
      enabled: !!orderId,
      onSuccess: (data) => {
        const { configuration, order_id, status } = data.getOrder;
        isHydratingRef.current = true;
        autosaveVersionRef.current += 1;
        if (autosaveTimerRef.current !== null) {
          window.clearTimeout(autosaveTimerRef.current);
          autosaveTimerRef.current = null;
        }
        reset({
          ...configuration,
          closing_place: configuration.closing_place?._id,
          additional_fees_letter: configuration.additional_fees_letter ?? 'B',
          order_id,
          status,
        });
        hasHydratedRef.current = true;
        isHydratingRef.current = false;
      },
    }
  );

  const { mutate: updateOrder, ...updateOrderResponse } = useMutation(gqlUpdateOrder, {
    onSuccess: () => {
      updateOrderResponse.reset();
      alert('Order updated.', 'success');
      queryCache.invalidateQueries(['party-order', orderId]);
    },
    onError: () => {
      updateOrderResponse.reset();
      alert('Unable to update order.', 'error');
    },
  });

  useEffect(() => {
    const subscription = watch((_values, { name }) => {
      if (!hasHydratedRef.current || isHydratingRef.current || !name || !AUTOSAVE_FIELDS.has(name)) return;

      autosaveVersionRef.current += 1;
      const autosaveVersion = autosaveVersionRef.current;
      if (autosaveTimerRef.current !== null) window.clearTimeout(autosaveTimerRef.current);

      autosaveTimerRef.current = window.setTimeout(() => {
        autosaveTimerRef.current = null;
        trigger().then((isValid) => {
          if (!isValid || autosaveVersion !== autosaveVersionRef.current) return;

          const { order_id, closing_place, cash_only, ...rest } = getValues();
          const formData: Omit<DatesAndConfigurationForm, 'order_id' | 'cash_only'> = rest;
          if (closing_place !== '') formData.closing_place = closing_place;
          updateOrder({
            id: orderId,
            data: formData,
          });
        });
      }, AUTOSAVE_DELAY_MS);
    });

    return () => {
      subscription.unsubscribe();
      autosaveVersionRef.current += 1;
      if (autosaveTimerRef.current !== null) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [getValues, orderId, trigger, updateOrder, watch]);

  const { data: transactionsResponse } = useGetListings({ code: 'allowed_transactions' });
  const { data: workflowsResponse } = useGetListings({ code: 'allowed_workflows' });
  const { data: settelmentStatementsResponse } = useGetListings({ code: 'allowed_types' });

  const transactionTypes = transactionsResponse?.getListing.entries ?? [];
  const workflows = workflowsResponse?.getListing.entries ?? [];
  const settelmentStatements = settelmentStatementsResponse?.getListing.entries ?? [];

  // Build lookup maps from the single order response
  const estatesMap = new Map<string, EstateAddress>();
  configurationOrder?.getOrder.estates?.forEach((estate) => {
    estatesMap.set(estate._id, estate);
  });

  const loansMap = new Map<string, string>();
  configurationOrder?.getOrder.loans?.forEach((loan) => {
    if (loan.loan_number) {
      loansMap.set(loan._id, loan.loan_number);
    }
  });

  // Parse accounting entries for amounts using regex filter on client side
  const allAccounting = configurationOrder?.getOrder.accounting ?? [];
  const amountEntries = allAccounting.filter((entry) => AMOUNT_CODE_REGEX.test(entry.code));

  // Separate into property (purchase price) and loan entries
  const purchasePriceEntriesByProperty = new Map<string, ParsedAmountEntry>();
  const loanAmountEntriesByLoan = new Map<string, ParsedAmountEntry>();

  amountEntries.forEach((entry) => {
    const match = entry.code.match(AMOUNT_CODE_REGEX);
    if (!match) return;

    const prefix = match[1];
    const referenceId = entry.code.slice(prefix.length + 1);

    const parsedEntry: ParsedAmountEntry = {
      _id: entry._id,
      amount: entry.amount || 0,
      code: entry.code,
      kind: entry.kind || '',
      title: prefix === 'purchase_price' ? 'Purchase Price' : 'Loan Amount',
      referenceId,
    };

    if (prefix === 'purchase_price' && entry.kind === 'property') {
      purchasePriceEntriesByProperty.set(referenceId, parsedEntry);
    }

    if (prefix === 'loan_amount' && entry.kind !== 'property') {
      loanAmountEntriesByLoan.set(referenceId, parsedEntry);
    }
  });

  const compareReferences = (first: string, second: string) => (
    first.localeCompare(second, undefined, { numeric: true, sensitivity: 'base' })
  );

  const purchasePriceEntries = Array.from(purchasePriceEntriesByProperty.values())
    .sort((first, second) => compareReferences(
      estatesMap.get(first.referenceId)?.address?.street_address || '\uffff',
      estatesMap.get(second.referenceId)?.address?.street_address || '\uffff',
    ));
  const loanAmountEntries = Array.from(loanAmountEntriesByLoan.values())
    .sort((first, second) => compareReferences(
      loansMap.get(first.referenceId) || '\uffff',
      loansMap.get(second.referenceId) || '\uffff',
    ));
  useInfoUpdateEvent(
    () => queryCache.invalidateQueries(['order-configuration', orderId]),
    (data) => data.kind === 'updateOrderConfiguration' || data.kind === 'workerAccountingLedger',
  );
  return {
    transactionTypes,
    workflows,
    methods,
    parties: configurationOrder?.getOrder.parties || [],
    previousclosingPlace: configurationOrder?.getOrder.configuration.closing_place,
    settelmentStatements,
    estatesMap,
    loansMap,
    purchasePriceEntries,
    loanAmountEntries,
  };
};

export default useDatesAndConfig;
